import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle2, Clock, Play, AlertCircle } from 'lucide-react';
import { cn, getInitials, formatDate, priorityConfig } from '../lib/utils';
import { taskAPI } from '../lib/api';
import { toast } from 'sonner';
import TaskDetailFullModal from './TaskDetailFullModal';
import { useWorkspace } from '../context/WorkspaceContext';

const STATUS_GROUPS = [
    { key: 'Not Started', label: 'Not Started', icon: AlertCircle, color: 'text-gray-500' },
    { key: 'Active', label: 'Active', icon: Play, color: 'text-blue-500' },
    { key: 'Review', label: 'Review', icon: Clock, color: 'text-yellow-500' },
    { key: 'Done', label: 'Done', icon: CheckCircle2, color: 'text-green-500' },
];

export default function SpaceTasksSlideOver({ isOpen, onClose, space, members, filterByMember }) {
    const { currentWorkspace } = useWorkspace();
    const [activeTab, setActiveTab] = useState('Not Started');
    const [tasks, setTasks] = useState({});
    const [loading, setLoading] = useState({});
    const [selectedTask, setSelectedTask] = useState(null);
    const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);

    useEffect(() => {
        if (isOpen && space && currentWorkspace) {
            console.log('Loading tasks for space:', space.name);
            // Load all status groups
            STATUS_GROUPS.forEach(group => {
                loadTasks(group.key);
            });
        }
    }, [isOpen, space, currentWorkspace, filterByMember]);

    const loadTasks = async (statusGroup) => {
        if (!space || !currentWorkspace) return;
        
        setLoading(prev => ({ ...prev, [statusGroup]: true }));
        try {
            console.log(`Loading ${statusGroup} tasks for space ${space.id}`);
            
            const params = {
                group: statusGroup,
                include_subtasks: true,
            };
            
            // If filterByMember is provided, filter by assignee instead of space
            if (filterByMember) {
                params.assignee_id = filterByMember;
            } else {
                params.space_id = space.id;
            }
            
            const response = await taskAPI.getByWorkspace(currentWorkspace.id, params);
            
            console.log(`${statusGroup} tasks loaded:`, response.data?.length || 0);
            setTasks(prev => ({
                ...prev,
                [statusGroup]: response.data || []
            }));
        } catch (error) {
            console.error(`Failed to load ${statusGroup} tasks:`, error);
            toast.error(`Gagal memuat tasks ${statusGroup}`);
        } finally {
            setLoading(prev => ({ ...prev, [statusGroup]: false }));
        }
    };

    const handleTaskClick = async (task) => {
        console.log('Task clicked:', task.id, task.title);
        console.log('Task data:', JSON.stringify(task, null, 2));
        
        // Fetch full task details to ensure we have complete data
        try {
            const response = await taskAPI.get(task.id);
            console.log('Full task data fetched:', response.data);
            setSelectedTask(response.data);
            setIsTaskDetailOpen(true);
        } catch (error) {
            console.error('Failed to fetch task details:', error);
            toast.error('Gagal memuat detail task');
            // Fallback: use task from list
            setSelectedTask(task);
            setIsTaskDetailOpen(true);
        }
    };

    const handleTaskUpdate = () => {
        console.log('Task updated, reloading tab:', activeTab);
        // Reload tasks for current tab
        loadTasks(activeTab);
    };

    const handleCloseTaskDetail = () => {
        console.log('Closing task detail');
        setIsTaskDetailOpen(false);
        setSelectedTask(null);
    };

    if (!isOpen) return null;

    const currentGroup = STATUS_GROUPS.find(g => g.key === activeTab);
    const currentTasks = tasks[activeTab] || [];

    console.log(`Displaying ${activeTab} tab with ${currentTasks.length} tasks`);

    return (
        <>
            {/* Backdrop */}
            <div 
                className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-200"
                onClick={onClose}
            />

            {/* SlideOver Panel - Lighter Animation */}
            <div 
                className="fixed top-0 right-0 h-full w-full md:w-[600px] lg:w-[700px] bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-out"
                style={{
                    transform: isOpen ? 'translateX(0)' : 'translateX(100%)'
                }}
            >
                {/* Header */}
                <div className="flex-shrink-0 border-b border-gray-200 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div 
                                className="w-4 h-4 rounded-full flex-shrink-0"
                                style={{ backgroundColor: space?.color || '#2F6F63' }}
                            />
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">{space?.name}</h2>
                                <p className="text-sm text-gray-500">Task Management by Status Group</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Tabs - More Compact */}
                    <div className="flex gap-1.5 mt-4 overflow-x-auto pb-1">
                        {STATUS_GROUPS.map(group => {
                            const Icon = group.icon;
                            const count = tasks[group.key]?.length || 0;
                            
                            return (
                                <button
                                    key={group.key}
                                    onClick={() => {
                                        console.log('Switching to tab:', group.key);
                                        setActiveTab(group.key);
                                    }}
                                    className={cn(
                                        "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap",
                                        activeTab === group.key
                                            ? "bg-brand-primary text-white shadow-sm"
                                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                    )}
                                >
                                    <Icon className="w-3.5 h-3.5" />
                                    <span>{group.label}</span>
                                    <span className={cn(
                                        "px-1.5 py-0.5 rounded-full text-[10px] font-semibold min-w-[18px] text-center",
                                        activeTab === group.key
                                            ? "bg-white/20 text-white"
                                            : "bg-gray-200 text-gray-700"
                                    )}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Content - Infinite Scroll Area */}
                <div className="flex-1 overflow-y-auto">
                    {loading[activeTab] ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                        </div>
                    ) : currentTasks.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center p-8">
                            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                {currentGroup && <currentGroup.icon className={cn("w-8 h-8", currentGroup.color)} />}
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                No Tasks in {activeTab}
                            </h3>
                            <p className="text-sm text-gray-500">
                                Belum ada task dengan status group ini di space {space?.name}
                            </p>
                        </div>
                    ) : (
                        <div className="p-4 space-y-3">
                            {currentTasks.map(task => (
                                <TaskCard 
                                    key={task.id} 
                                    task={task} 
                                    members={members}
                                    onClick={() => handleTaskClick(task)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Task Detail Modal */}
            {selectedTask && (
                <TaskDetailFullModal
                    isOpen={isTaskDetailOpen}
                    onClose={handleCloseTaskDetail}
                    task={selectedTask}
                    listId={selectedTask?.list_id}
                    statuses={[]}
                    onTaskUpdate={handleTaskUpdate}
                    members={members}
                    readOnly={false}
                />
            )}
        </>
    );
}

// Task Card Component - Simplified Layout
function TaskCard({ task, members, onClick }) {
    const assignees = task.assignee_ids
        ?.map(id => members.find(m => m.user_id === id))
        .filter(Boolean) || [];

    const priorityInfo = priorityConfig[task.priority] || priorityConfig.none;
    const firstAssignee = assignees[0];

    return (
        <div
            onClick={onClick}
            className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md hover:border-brand-primary transition-all cursor-pointer group"
        >
            {/* Row 1: Code + Title + Status */}
            <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {/* Task Code */}
                    {task.task_code && (
                        <span className="text-xs font-mono text-gray-600 font-semibold flex-shrink-0">
                            #{task.task_code}
                        </span>
                    )}
                    
                    {/* Title */}
                    <h4 className="text-sm font-medium text-gray-900 truncate group-hover:text-brand-primary transition-colors">
                        {task.title}
                    </h4>
                </div>

                {/* Status Badge */}
                {task.status && (
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded flex-shrink-0">
                        {task.status}
                    </span>
                )}
            </div>

            {/* Row 2: Assignee + Priority */}
            <div className="flex items-center justify-between text-xs">
                {/* Assignee */}
                <div className="flex items-center gap-1.5">
                    {firstAssignee ? (
                        <>
                            <div
                                className="w-5 h-5 rounded-full bg-brand-primary text-white text-[10px] flex items-center justify-center font-medium"
                                title={firstAssignee.user_name || firstAssignee.name}
                            >
                                {getInitials(firstAssignee.user_name || firstAssignee.name)}
                            </div>
                            <span className="text-gray-600 truncate max-w-[120px]">
                                {firstAssignee.user_name || firstAssignee.name}
                            </span>
                            {assignees.length > 1 && (
                                <span className="text-gray-400">+{assignees.length - 1}</span>
                            )}
                        </>
                    ) : (
                        <span className="text-gray-400 italic">Unassigned</span>
                    )}
                </div>

                {/* Priority Badge */}
                <div 
                    className="px-2 py-0.5 rounded text-xs font-medium flex-shrink-0"
                    style={{ 
                        backgroundColor: `${priorityInfo.color}15`,
                        color: priorityInfo.color 
                    }}
                >
                    {priorityInfo.label}
                </div>
            </div>
        </div>
    );
}
